import React, {useEffect, useRef, useState} from 'react';

import {useStore} from 'react-redux';
import ReconnectingWebSocket from 'reconnecting-websocket';

import {chatSubscriptionRegistry} from 'app/subscriptions/subscriptionRegistry';
import {WebsocketContext} from 'app/ws/context';
import {WebSocketRouter} from 'app/ws/ws-router';

const WEBSOCKET_OPEN_FLAG = 1;

interface Props {
  children: React.ReactNode;
  connectionUrl?: string;
}

export function ChatWebsocketProvider({
  children,
  connectionUrl = '/ws/',
}: Props) {
  const store = useStore();
  const socket = React.useRef<ReconnectingWebSocket | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const prevIsOpen = useRef(isOpen);

  const sendWebsocketMessage = React.useCallback((payload: string): void => {
    if (socket.current?.readyState === WEBSOCKET_OPEN_FLAG) {
      socket.current!.send(payload);
    }
  }, []);

  const chatUpdate = (event: string, {data}: {data: any}) => {
    return chatSubscriptionRegistry.process(data);
  };

  useEffect(() => {
    setIsClosing(prevIsOpen.current && !isOpen);
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const WEBSOCKET_URL = 'wss://' + window.location.host + connectionUrl;

    const router = new WebSocketRouter('chat-router');
    router.register_handler('chat-update', chatUpdate);

    function handleMessage(e: MessageEvent) {
      const content = JSON.parse(e.data);
      // convert old messages to new format
      // TODO: update backend code to use new format so we can remove this
      const message =
        content.action != null || content.payload != null
          ? {
              event: content.action,
              data: content.payload,
            }
          : content;

      router.route(message.event, message, sendWebsocketMessage, store);
    }

    if (window.WebSocket) {
      // eslint-disable-next-line
      // @ts-ignore ReconnectiongWebSocket is fine but the types are wrong?
      socket.current = new ReconnectingWebSocket(WEBSOCKET_URL, [], {
        connectionTimeout: 10000,
        maxReconnectionDelay: 20000,
        minReconnectionDelay: 3000,
      });
      socket.current!.onopen = () => setIsOpen(true);
      socket.current!.onclose = () => setIsOpen(false);
      socket.current!.onerror = () => setIsOpen(false);

      socket.current!.onmessage = handleMessage;

      return () => {
        if (socket.current) {
          // @ts-expect-error I am calling the ``close`` method from
          // ReconnectingWebSocket, not from window.WebSocket.
          socket.current.close(1000, '', {
            keepClosed: true,
            fastClose: true,
            delay: 0,
          });
        }
      };
    }
  }, []);

  return (
    <WebsocketContext.Provider
      value={{
        sendWebsocketMessage,
        isWSOpen: isOpen,
        isWSClosing: isClosing,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
}
